import { CalendarFragmentKey, CalendarLayoutConfig, CALENDAR_LAYOUT_VERSION, FragmentConfig, LayoutZone, SurfaceLayout } from './model';

/**
 * The built-in layouts. These reproduce the hand-written markup the card and the detail popover had
 * before they became configurable, so an installation that has never been configured looks exactly
 * as it did. They are also the floor the "reset" action falls back to when no system default has
 * been saved.
 */

const f = (key: CalendarFragmentKey): FragmentConfig => ({ key });

export const BUILT_IN_CARD_LAYOUT: SurfaceLayout = {
  // `Title` rather than OnBehalfOf + Name: the original showed one or the other, never both.
  [LayoutZone.HeaderLeft]: [f(CalendarFragmentKey.WorkflowIcon), f(CalendarFragmentKey.Title), f(CalendarFragmentKey.Flags)],
  [LayoutZone.HeaderRight]: [f(CalendarFragmentKey.ActionTypes)],
  [LayoutZone.Body]: [
    f(CalendarFragmentKey.Labels),
    f(CalendarFragmentKey.CarrierInfo),
    f(CalendarFragmentKey.CargoItem),
    f(CalendarFragmentKey.Created),
    f(CalendarFragmentKey.Updated),
    f(CalendarFragmentKey.SlotDuration),
  ],
};

export const BUILT_IN_DETAIL_LAYOUT: SurfaceLayout = {
  [LayoutZone.HeaderLeft]: [f(CalendarFragmentKey.Title)],
  [LayoutZone.HeaderRight]: [f(CalendarFragmentKey.Duration)],
  [LayoutZone.Body]: [
    f(CalendarFragmentKey.WorkflowState),
    f(CalendarFragmentKey.Labels),
    f(CalendarFragmentKey.ActionTypes),
    f(CalendarFragmentKey.Flags),
    f(CalendarFragmentKey.Location),
    f(CalendarFragmentKey.CarrierInfo),
    f(CalendarFragmentKey.TimeRange),
    f(CalendarFragmentKey.CargoItem),
    f(CalendarFragmentKey.Created),
    f(CalendarFragmentKey.Updated),
  ],
};

/**
 * Timestamp of the built-in config. A fixed value rather than "now" so the object is referentially
 * stable across renders and two built-in configs always compare equal.
 */
const BUILT_IN_UPDATED_AT = '1970-01-01T00:00:00.000Z';

export const BUILT_IN_LAYOUT_CONFIG: CalendarLayoutConfig = {
  version: CALENDAR_LAYOUT_VERSION,
  updatedAt: BUILT_IN_UPDATED_AT,
  card: BUILT_IN_CARD_LAYOUT,
  detail: BUILT_IN_DETAIL_LAYOUT,
};

export const isBuiltInLayout = (config: CalendarLayoutConfig | null | undefined): boolean => config?.updatedAt === BUILT_IN_UPDATED_AT;

/**
 * Fragments that render unconditionally today, independent of the legacy `CALENDAR_CARD_FRAGMENTS`
 * list. They have to be re-inserted when adapting that list, or migrating a company that has one
 * saved would silently drop flags, action types and the on-behalf-of reference line.
 */
const LEGACY_ALWAYS_ON = {
  headerLeft: [CalendarFragmentKey.Flags],
  headerRight: [CalendarFragmentKey.ActionTypes],
};

const LEGACY_HEADER_LEFT = new Set<string>([CalendarFragmentKey.WorkflowIcon, CalendarFragmentKey.Name]);
const LEGACY_BODY = new Set<string>([
  CalendarFragmentKey.Labels,
  CalendarFragmentKey.CarrierInfo,
  CalendarFragmentKey.CargoItem,
  CalendarFragmentKey.Created,
  CalendarFragmentKey.Updated,
]);

/**
 * Adapts the legacy flat, comma-separated fragment list into the zone model.
 *
 * Applied to the historical default list this reproduces the previous DOM for a reservation without
 * on-behalf-of. Two deliberate simplifications: `CFRApplicationId` leads the body (previously it
 * landed above or below the title depending on its index relative to `CFRName`), and on-behalf-of
 * is placed as its own header fragment ahead of the reference — the legacy list has no key for it
 * because it used to be a branch inside `CFRName` rather than a field of its own.
 */
export const layoutFromLegacyFragments = (fragments: string[]): SurfaceLayout => {
  const known = fragments.map(t => t.trim()).filter(Boolean);
  const has = (key: CalendarFragmentKey) => known.includes(key);

  const body: FragmentConfig[] = [
    ...(has(CalendarFragmentKey.ApplicationId) ? [f(CalendarFragmentKey.ApplicationId)] : []),
    ...known.filter(key => LEGACY_BODY.has(key)).map(key => f(key as CalendarFragmentKey)),
    ...(has(CalendarFragmentKey.SlotDuration) ? [f(CalendarFragmentKey.SlotDuration)] : []),
  ];

  const headerLeft: FragmentConfig[] = [];
  known.forEach(key => {
    if (!LEGACY_HEADER_LEFT.has(key)) return;
    // `CFRName` in the legacy list means the old title, which showed on-behalf-of in place of the
    // reference when the reservation had one — that is `Title`, not `Name`.
    headerLeft.push(f(key === CalendarFragmentKey.Name ? CalendarFragmentKey.Title : (key as CalendarFragmentKey)));
  });
  // A list without CFRName still showed the company when acting on behalf of one.
  if (!headerLeft.some(item => item.key === CalendarFragmentKey.Title)) headerLeft.push(f(CalendarFragmentKey.Title));
  headerLeft.push(...LEGACY_ALWAYS_ON.headerLeft.map(f));

  return {
    [LayoutZone.HeaderLeft]: headerLeft,
    [LayoutZone.HeaderRight]: LEGACY_ALWAYS_ON.headerRight.map(f),
    [LayoutZone.Body]: body,
  };
};
